import { useEffect, useRef, useState } from 'react'

import { submitIntakeForm } from '../shared/api'
import './intake.css'

const STORAGE_KEY = 'work-dashboards-intake-submission-id'
const FORM_ID = /^form:[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f-\u009f]/g
const EMPTY_FIELDS = {
  student_english_name: '',
  grade: '',
  school: '',
  parent_name: '',
  wechat_or_phone: '',
  area_of_interest: '',
  experience: '',
  interview_time: '',
  main_goal: '',
  referred_by: '',
}

const FIELDS = [
  ['student_english_name', '学生英文名 | Student English Name', true],
  ['grade', '年级 | Grade', true],
  ['school', '学校 | School', false],
  ['parent_name', '家长姓名 | Parent Name', false],
  [
    'wechat_or_phone',
    '家长微信号或手机号（可选）| Parent WeChat ID or phone number (optional)',
    false,
  ],
  ['experience', '相关经验描述（可选）| Describe your experience (optional)', false, 'textarea'],
  ['interview_time', '适合面试时间（可选）| Best times for an interview (optional)', false],
  ['main_goal', '目标描述（可选）| Describe your goal (optional)', false, 'textarea'],
  ['referred_by', '推荐人（可选）| Referred by whom (optional)', false],
]

function randomUuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  const bytes = new Uint8Array(16)
  window.crypto?.getRandomValues?.(bytes)
  if (!bytes.some(Boolean)) {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256)
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function mintSubmissionId() {
  return `form:${randomUuid()}`
}

function getOrCreateSubmissionId() {
  let existing = null
  try {
    existing = window.sessionStorage.getItem(STORAGE_KEY)
  } catch {
    // Storage can be denied in embedded WeChat/WebView privacy modes.
  }
  if (existing && FORM_ID.test(existing)) return existing
  const created = mintSubmissionId()
  persistSubmissionId(created)
  return created
}

function persistSubmissionId(id) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, id)
  } catch {
    // React state remains the source for this mounted form session.
  }
}

function errorCopy(error) {
  if (error?.status === 503 || error?.code === 'intake_form_unavailable') {
    return '表格暂时无法使用，请在现有微信对话中联系 Matt。 | The form is temporarily unavailable. Please message Matt in your existing WeChat chat.'
  }
  if (error?.status === 429) {
    return '提交过于频繁，请稍后重试。 | Too many submissions. Please try again later.'
  }
  return '提交失败，请检查网络后重试；也可以在微信中联系 Matt。 | Submission failed. Please check your connection and retry, or message Matt on WeChat.'
}

function hasPersistedContact(value) {
  return value.replace(CONTROL_CHARACTERS, '').trim().length > 0
}

export default function IntakeForm() {
  const [fields, setFields] = useState(EMPTY_FIELDS)
  const [submissionId, setSubmissionId] = useState(getOrCreateSubmissionId)
  const [formOpenedAt] = useState(() => new Date().toISOString())
  const [website, setWebsite] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [complete, setComplete] = useState(false)
  const [usedContactChannel, setUsedContactChannel] = useState(false)
  const successHeadingRef = useRef(null)

  useEffect(() => {
    if (complete) successHeadingRef.current?.focus()
  }, [complete])

  const setField = (name) => (event) => {
    setFields((current) => ({ ...current, [name]: event.target.value }))
    setError(null)
  }

  async function handleSubmit(event) {
    event.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const response = await submitIntakeForm({
        ...fields,
        submission_id: submissionId,
        form_opened_at: formOpenedAt,
        website,
      })
      setUsedContactChannel(Boolean(
        hasPersistedContact(fields.wechat_or_phone)
        && !website
        && response.already_received !== true
      ))
      const nextId = mintSubmissionId()
      persistSubmissionId(nextId)
      setSubmissionId(nextId)
      setComplete(true)
    } catch (cause) {
      setError(cause)
    } finally {
      setSubmitting(false)
    }
  }

  if (complete) {
    return (
      <main className="intake-page">
        <section className="intake-card intake-success" aria-live="polite">
          <div className="intake-success-mark" aria-hidden="true">✓</div>
          <h1 ref={successHeadingRef} tabIndex={-1}>提交成功 | Form received</h1>
          {usedContactChannel ? (
            <p>我们会通过微信或电话与您联系。 | We&apos;ll contact you on WeChat or by phone.</p>
          ) : (
            <p>如需联系我们，请在现有微信对话中联系 Matt。 | To reach us, please message Matt in your existing WeChat chat.</p>
          )}
        </section>
      </main>
    )
  }

  return (
    <main className="intake-page">
      <section className="intake-card">
        <header className="intake-heading">
          <p className="intake-eyebrow">Work Dashboards</p>
          <h1>课程咨询表 | Program Intake Form</h1>
          <p>请填写以下信息，我们会认真了解学生的需求。 | Tell us a little about the student and their goals.</p>
        </header>

        {error && (
          <div className="intake-error" role="alert">
            <strong>{errorCopy(error)}</strong>
            {error?.payload?.errors?.map((item) => (
              <span key={item.field}>{item.message_zh} | {item.message}</span>
            ))}
          </div>
        )}

        <form className="intake-form" onSubmit={handleSubmit}>
          {FIELDS.slice(0, 5).map(([name, label, required, kind]) => (
            <label className="intake-field" key={name}>
              <span>{label}{required && <span className="intake-required" aria-hidden="true"> *</span>}</span>
              {kind === 'textarea' ? (
                <textarea name={name} value={fields[name]} onChange={setField(name)} required={required} />
              ) : (
                <input name={name} value={fields[name]} onChange={setField(name)} required={required} />
              )}
            </label>
          ))}

          <label className="intake-field">
            <span>兴趣方向 | Area of Interest<span className="intake-required" aria-hidden="true"> *</span></span>
            <select name="area_of_interest" value={fields.area_of_interest} onChange={setField('area_of_interest')} required>
              <option value="">请选择 | Please select</option>
              <option value="Speech">演讲 | Speech</option>
              <option value="Debate">辩论 | Debate</option>
              <option value="Both">演讲和辩论 | Both</option>
            </select>
          </label>

          {FIELDS.slice(5).map(([name, label, required, kind]) => (
            <label className="intake-field" key={name}>
              <span>{label}{required && <span className="intake-required" aria-hidden="true"> *</span>}</span>
              {kind === 'textarea' ? (
                <textarea name={name} value={fields[name]} onChange={setField(name)} required={required} />
              ) : (
                <input name={name} value={fields[name]} onChange={setField(name)} required={required} />
              )}
            </label>
          ))}

          <label className="intake-honeypot" aria-hidden="true">
            Website
            <input name="website" value={website} onChange={(event) => setWebsite(event.target.value)} tabIndex={-1} autoComplete="off" />
          </label>

          <p className="intake-privacy">您的信息仅用于课程咨询和安排。 | Your information is used only for program consultation and scheduling.</p>
          <button className="intake-submit" type="submit" disabled={submitting}>
            {submitting ? '正在提交… | Submitting…' : error ? '重试 | Retry' : '提交 | Submit'}
          </button>
        </form>
      </section>
    </main>
  )
}
